import { dtTokenScope, dtFocusRing } from './css-recipes';

/**
 * DevTools panel CSS — shipped as an injected style string (the platform build
 * has no css-loader, so an SFC `<style>` block cannot be parsed).
 *
 * Design contract lives in `css-recipes.ts`: this file consumes `var(--dt-*)`
 * exclusively — no raw host tokens, no per-property fallbacks.
 *
 * `scoped` is gone, so every selector is namespaced under `.abax-devtools-panel`
 * (the panel root) to prevent leakage.
 */
export const DEV_TOOLS_PANEL_CSS = /* css */ `
${dtTokenScope('.abax-devtools-panel')}

.abax-devtools-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99999998;
  font-family: var(--font-sans, system-ui, sans-serif);
  font-size: var(--dt-text-base);
}

.abax-devtools-panel .dev-tools-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  bottom: 0;
  right: 16px;
  min-height: 44px;
  padding: 0 12px;
  background: var(--dt-bg);
  color: var(--dt-fg);
  border: 1px solid var(--dt-border);
  border-bottom: none;
  border-radius: var(--dt-radius) var(--dt-radius) 0 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.18);
  font-size: var(--dt-text-sm);
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: background var(--dt-dur-fast) var(--dt-ease), color var(--dt-dur-fast) var(--dt-ease),
    box-shadow var(--dt-dur-fast) var(--dt-ease);
  touch-action: none;
}

.abax-devtools-panel .dev-tools-toggle:hover {
  background: color-mix(in srgb, var(--dt-fg) 5%, var(--dt-bg));
}

.abax-devtools-panel .dev-tools-toggle__main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  cursor: grab;
}

.abax-devtools-panel .dev-tools-toggle__main:active {
  cursor: grabbing;
}

.abax-devtools-panel.is-open .dev-tools-toggle__main {
  cursor: pointer;
}

${dtFocusRing('.abax-devtools-panel .dev-tools-toggle__main')}
.abax-devtools-panel .dev-tools-toggle__main:focus-visible {
  border-radius: var(--dt-radius-sm);
}

.abax-devtools-panel .dev-tools-toggle__caret {
  display: inline-flex;
  line-height: 1;
  color: var(--dt-muted-fg);
}

.abax-devtools-panel .dev-tools-toggle:hover .dev-tools-toggle__caret {
  color: var(--dt-fg);
}

.abax-devtools-panel .dev-tools-toggle__indicators {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
  border-left: 1px solid var(--dt-border);
}

.abax-devtools-panel.is-open .dev-tools-toggle {
  bottom: auto;
  top: 0;
  transform: translateY(-100%);
  box-shadow: none;
}

.abax-devtools-panel .dev-tools-content {
  background: var(--dt-bg);
  color: var(--dt-fg);
  border-top: 1px solid var(--dt-border);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
}

/* Visual strip is 6px; '::before' widens the pointer hit area without
   shifting layout, and the centered grip pill signals draggability. */
.abax-devtools-panel .resize-handle {
  position: relative;
  height: 6px;
  cursor: ns-resize;
  background: transparent;
  flex-shrink: 0;
  transition: background var(--dt-dur-fast) var(--dt-ease);
}

.abax-devtools-panel .resize-handle::before {
  content: '';
  position: absolute;
  inset: -5px 0;
}

.abax-devtools-panel .resize-handle::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 36px;
  height: 3px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  background: var(--dt-border);
  transition: background var(--dt-dur-fast) var(--dt-ease);
}

.abax-devtools-panel .resize-handle:hover {
  background: color-mix(in srgb, var(--dt-accent) 12%, transparent);
}

.abax-devtools-panel .resize-handle:hover::after,
.abax-devtools-panel .resize-handle:active::after,
.abax-devtools-panel .resize-handle:focus-visible::after {
  background: var(--dt-accent);
}

.abax-devtools-panel .resize-handle:active,
.abax-devtools-panel .resize-handle:focus-visible {
  background: color-mix(in srgb, var(--dt-accent) 25%, transparent);
}

${dtFocusRing('.abax-devtools-panel .resize-handle', '0')}

.abax-devtools-panel .tabs-row {
  display: flex;
  background: var(--dt-bg);
  border-bottom: 1px solid var(--dt-border);
  flex-shrink: 0;
}

.abax-devtools-panel .dev-tools-tabs {
  display: flex;
  gap: 0;
  padding: 0 8px;
  flex: 1;
  min-width: 0;
}

.abax-devtools-panel .tab-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  color: var(--dt-muted-fg);
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--dt-text-sm);
  font-weight: 500;
  transition: color var(--dt-dur-fast) var(--dt-ease),
    border-color var(--dt-dur-fast) var(--dt-ease),
    background var(--dt-dur-fast) var(--dt-ease);
}

.abax-devtools-panel .tab-button:hover {
  color: var(--dt-fg);
  background: color-mix(in srgb, var(--dt-fg) 4%, transparent);
}

${dtFocusRing('.abax-devtools-panel .tab-button', '-2px')}

.abax-devtools-panel .tab-button.active {
  color: var(--dt-fg);
  border-bottom-color: var(--dt-accent);
}

.abax-devtools-panel .tab-button__icon {
  display: inline-flex;
  flex-shrink: 0;
}

.abax-devtools-panel .tab-content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  background: var(--dt-surface);
}

.abax-devtools-panel .tab-panel {
  height: 100%;
  min-height: 0;
}

${dtFocusRing('.abax-devtools-panel .tab-panel', '-2px')}

.abax-devtools-panel .inspector-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
}

.abax-devtools-panel .dev-tools-empty {
  margin: 0;
  padding: 16px;
  color: var(--dt-muted-fg);
  font-size: var(--dt-text-sm);
  text-align: center;
}

.abax-devtools-panel .dev-tools-slide-enter-active,
.abax-devtools-panel .dev-tools-slide-leave-active {
  transition: transform var(--dt-dur-slow) var(--dt-ease), opacity var(--dt-dur) ease-out;
  transform-origin: bottom;
  will-change: transform, opacity;
}

.abax-devtools-panel .dev-tools-slide-enter-from {
  transform: translateY(12px) scaleY(0.985);
  opacity: 0;
}

.abax-devtools-panel .dev-tools-slide-leave-to {
  transform: translateY(8px) scaleY(0.99);
  opacity: 0;
}
`;
